import React, { useMemo, useRef, memo } from 'react';
import PropTypes from 'prop-types';

import AnswerPart from './AnswerPart.jsx';
import ThinkingPart from './ThinkingPart.jsx';
import ToolUsePart from './ToolUsePart.jsx';
import UserPart from './UserPart.jsx';

const ChatBubble = ({ message, index, isStreaming = false, isFocused = false, isLast = false, isGenerating = false, onRetry }) => {
  const isUser = message.role === 'user';
  const bubbleRef = useRef(null);

  const flatParts = useMemo(() => {
    if (!message.message_parts || message.message_parts.length === 0) {
      return [];
    }

    const toolResultMap = new Map();
    for (const part of message.message_parts) {
      if (part.type === 'tool_result' && part.tool_use_id) {
        toolResultMap.set(part.tool_use_id, part);
      }
    }

    const parts = [];
    let currentAnswerGroup = null;

    for (const part of message.message_parts) {
      if (part.type === 'tool_result') continue;

      if (part.type === 'answer') {
        if (currentAnswerGroup) {
          currentAnswerGroup.content += (part.content || '');
        } else {
          currentAnswerGroup = { type: 'answer', content: part.content || '' };
        }
      } else {
        if (currentAnswerGroup) {
          parts.push(currentAnswerGroup);
          currentAnswerGroup = null;
        }

        if (part.type === 'tool_use') {
          const toolResult = part.tool_use_id ? toolResultMap.get(part.tool_use_id) : null;
          parts.push({ ...part, toolResult: toolResult || null });
        } else {
          parts.push(part);
        }
      }
    }

    if (currentAnswerGroup) {
      parts.push(currentAnswerGroup);
    }

    return parts;
  }, [message.message_parts]);

  /* RENDER */
  const showPlaceholder = !isUser && isStreaming && 
    (!message.message_parts || message.message_parts.length === 0);
  
  if (!isUser && !message.message_parts?.length && !showPlaceholder) return null;

  return (
    <>
      {flatParts.map((part, idx) => (
        <div className="flex justify-start w-full mb-spacing-2" key={`${part.type}-${idx}`}>
          {part.type === 'thinking' && (
            <ThinkingPart thinking={part.content} duration={part.duration_ms} isStreaming={isStreaming} />
          )}
          {part.type === 'tool_use' && (
            <ToolUsePart part={part} isStreaming={isStreaming} />
          )}
          {part.type === 'answer' && (
            <AnswerPart content={part.content} />
          )}
        </div>
      ))}

      <div
        className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-spacing-2`}
        data-message-id={message.id}
        data-message-role={message.role}
        data-is-user={isUser}
      >
        <div className="flex items-end space-x-2 max-w-full flex-col">
          {isUser ? (
            <>
              <UserPart message={message} isFocused={isFocused} />
              {isLast && !isStreaming && !isGenerating && onRetry && message.metadata?.orphaned && (
                <button
                  onClick={onRetry}
                  className="flex items-center gap-1.5 mt-1 px-2 py-1 text-xs text-secondary hover:text-primary rounded hover:bg-bg-2 transition-colors"
                  title="Retry this message"
                >
                  <svg data-testid="geist-icon" height="16" strokeLinejoin="round" viewBox="0 0 16 16" width="16" style={{ color: 'currentcolor' }}>
                    <path fillRule="evenodd" clipRule="evenodd" d="M8.00002 1.25C5.33749 1.25 3.02334 2.73677 1.84047 4.92183L1.48342 5.58138L2.80253 6.29548L3.15958 5.63592C4.09084 3.91566 5.90986 2.75 8.00002 2.75C10.4897 2.75 12.5941 4.40488 13.2713 6.67462H11.8243H11.0743V8.17462H11.8243H15.2489C15.6631 8.17462 15.9989 7.83883 15.9989 7.42462V4V3.25H14.4989V4V5.64468C13.4653 3.06882 10.9456 1.25 8.00002 1.25ZM1.50122 10.8555V12.5V13.25H0.0012207V12.5V9.07538C0.0012207 8.66117 0.337007 8.32538 0.751221 8.32538H4.17584H4.92584V9.82538H4.17584H2.72876C3.40596 12.0951 5.51032 13.75 8.00002 13.75C10.0799 13.75 11.8912 12.5958 12.8266 10.8895L13.1871 10.2318L14.5025 10.9529L14.142 11.6105C12.9539 13.7779 10.6494 15.25 8.00002 15.25C5.05453 15.25 2.53485 13.4313 1.50122 10.8555Z" fill="currentColor"></path>
                  </svg>
                  Retry
                </button>
              )}
            </>
          ) : showPlaceholder ? (
            <div className="bg-bg-message-ai py-spacing-3 px-spacing-4 rounded-lg min-h-spacing-12 flex items-center" ref={bubbleRef}>
              <div className="animate-pulse h-2 w-2 bg-bg-active rounded-full" />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

ChatBubble.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    role: PropTypes.string.isRequired,
    content: PropTypes.string,
    message_parts: PropTypes.array,
    metadata: PropTypes.shape({
      images: PropTypes.array,
      usage_log: PropTypes.shape({
        thinking_time: PropTypes.number,
      }),
    }),
  }).isRequired,
  index: PropTypes.number.isRequired,
  isStreaming: PropTypes.bool,
  isFocused: PropTypes.bool,
  isLast: PropTypes.bool,
  isGenerating: PropTypes.bool,
  onRetry: PropTypes.func,
};

export default memo(ChatBubble, (prevProps, nextProps) => {
  return (
    prevProps.message === nextProps.message &&
    prevProps.index === nextProps.index &&
    prevProps.isStreaming === nextProps.isStreaming &&
    prevProps.isFocused === nextProps.isFocused &&
    prevProps.isLast === nextProps.isLast &&
    prevProps.isGenerating === nextProps.isGenerating &&
    prevProps.onRetry === nextProps.onRetry
  );
});
